<script setup lang="ts">
import type { Brand } from '@design-system/index'
import type { extSize } from '@design-system/components/Pdp/PdpSizeSelector.props'
import type { Price } from '@commercelayer/sdk'
import type { ListResponse } from '@commercelayer/sdk'
import type { PdpEcommerceBlockProductFocusSideSlideProps } from '@design-system/components/Pdp/EcommerceBlock/PdpEcommerceBlockProductFocusSideSlide.props'
import type { PdpMeta } from '@integration-layer/composables/useAmplienceSeo'
import { productSchema } from '@integration-layer/.nuxt/armani/product-schema'
import { useParamPage } from '@integration-layer/composables/useGAEvent/useDatalayerSetup/useParamPage'
import { z } from 'zod'
import { withoutTrailingSlash } from 'ufo'
import startCase from 'lodash/startCase'
import { useAlgoliaInsights } from '@integration-layer/composables/useAlgoliaInsights'
import { useSizebayCountry } from '@integration-layer/composables/useSizebayCountry'
import { toPublicLocalePath } from '@design-system/utils/publicLocalePath'
import {
  getMicroCatKind,
  isSingleSizeMicroCat,
} from '@design-system/utils/microCategories'

// // map and update the productCode based on the slug and manual updates
const route = useRoute()
const { country } = useRouteHelper()

const { getAlgoliaProductIdFromSlug } = useProductUrls()
const extSizesFormatted = ref<extSize[]>([])
const thisProductDims = ref('')
const { renderExtSizesFormatted, productDims } = usePdpExtSizesUtils()
const selectedSizeState = useState<extSize>(
  'selectedSizeState',
  () => ({}) as extSize
)
const { viewProduct, searchQueryID, addedToCartfromPDP } = useAlgoliaInsights()
const { dispatchAddToCartEvent } = useGACartEvents()

const productSlug = computed(
  () => removeLastEmptyParam(route.params.slug!).at(-1)!
)
const productCode = ref(getAlgoliaProductIdFromSlug(productSlug.value))

watch(productSlug, newVal => {
  productCode.value = getAlgoliaProductIdFromSlug(newVal)
})

//fetch product data and update it based on productCode
const { fetchAndMapProductData, productDataState: pdpProductData } =
  useTemplatePdp()

await fetchAndMapProductData(productCode.value, {
  updateState: true,
  skipIfHydrated: true,
})

if (!pdpProductData.value) {
  throw createError({
    statusCode: 404,
    fatal: true,
    statusMessage: `[ERROR]: Product data is not available`,
    message: `[ERROR]: Product data is not available`,
  })
}

// product resolved via stable code but slug is stale (e.g. product renamed):
// 301 redirect to the current canonical path instead of 404.
// Compare public paths: route.path may be internal (/en-wx) while correctPath is /en.
const correctPath: string | null = pdpProductData.value?.productPath ?? null
const routePath = toPublicLocalePath(route.path)
const isStalePath =
  !!correctPath &&
  withoutTrailingSlash(correctPath) !== withoutTrailingSlash(routePath)
if (isStalePath) {
  navigateTo(correctPath, { redirectCode: 301 })
}

const {
  title,
  brand,
  computedLists,
  selectedColor,
  sizes: skuSizes,
  hasExtSizes,
  orderedGallery,
  rawImages,
  raw3DModels,
  amountFloat,
  currencyCode,
  price,
  discount,
  oldPrice,
  mainCategory,
  productPathWithoutLocale,
  productDetails,
  productCategoriesSlugs,
  productGender,
  categories,
  sizesData,
  editorialDeliveryKeys,
  alternateUrls,
  infoCommerce,
  MFC,
  SizeGridCode,
  editorialProductFocus,
  finalSales,
  isFriendsFamily,
  isFallback,
  commercialMetaTitle,
  commercialMetaDescription,
} = toRefs(pdpProductData.value)

/**
 * Gallery is frozen into a local state to avoid displaying the images before the page has unmounted.
 * It's not pretty, but the alternative was a full PDP logic refactor.
 */
const frozenGallery = shallowRef(pdpProductData.value?.orderedGallery)

const schema3DModelURLs = computed(() => {
  return raw3DModels.value.map(i => ({
    '@type': '3DModel',
    name: `${title} - 3D Model`,
    contentUrl: i,
  }))
})

useHead(() => ({
  link: [...alternateUrls.value],
  ...(isFallback?.value && {
    meta: [{ name: 'robots', content: 'noindex, nofollow' }],
  }),
}))

const updateExtSizesAndDimsInfo = async () => {
  //if there are cached ext sizes, use the cached, otherwise fetch them
  extSizesFormatted.value = await renderExtSizesFormatted(
    productCode.value,
    orderedGallery.value
  )
  thisProductDims.value = productDims.value
}

const mountedPdp = ref(false)

// update CL data and fetch extSizes on client (it's information coming from CommerceLayer)
onMounted(async () => {
  mountedPdp.value = true

  // if (hasExtSizes.value) {
  // Get selectedSizeState from local storage (for extSizes with same global SKU after refresh)
  // i need to load the previous selected size object after refresh when the sku is the same, to avoid
  // adding the wrong size to cart after mount.
  // Right now its not needed because we reset to default size after refresh.

  // const storedSelectedSku = localStorage.getItem('selectedSizeState')
  // const [storedSKU, storedSkuWithSize] = storedSelectedSku?.split('-') ?? []
  // const selectedSizeObj = extSizesFormatted.value.find(
  //   size => size.skuWithSize === storedSkuWithSize
  // )
  // if (storedSelectedSku && selectedSizeState.value) {
  //   try {
  //     if (storedSKU === productCode.value && selectedSizeObj) {
  //       selectedSizeState.value = selectedSizeObj
  //       localStorage.removeItem('selectedSizeState')
  //     }
  //   } catch (error) {
  //     console.error(
  //       'Error parsing selectedSizeState from localStorage:',
  //       error
  //     )
  //   }
  // }
  // }
})

const sizeList = computed(() => computedLists.value.sizeList)

const microCatKind = computed(() =>
  getMicroCatKind(pdpProductData.value?.microCatEn)
)

const isSingleSizeByMicroCat = computed(() =>
  isSingleSizeMicroCat(
    microCatKind.value,
    sizeList.value?.length ?? 0,
    hasExtSizes.value
  )
)

const { sizes, selectedSizeIndex, selectedCountry, selectedSize } =
  useSizeSelector(
    sizeList,
    hasExtSizes.value,
    sizesData.value,
    isSingleSizeByMicroCat.value
  )

watch(sizes, sizes => {
  if (
    sizes &&
    selectedSizeIndex.value !== null &&
    sizes[selectedCountry.value][selectedSizeIndex.value].disabled
  ) {
    selectedSizeIndex.value = null
  }
})

const loadNewExtProd = async (prod: string | undefined) => {
  if (!prod) return
  productCode.value = prod
  await fetchAndMapProductData(prod, {
    updateState: true,
  })

  frozenGallery.value = pdpProductData.value?.orderedGallery ?? []

  // to get correct sku after ext size change
  if (hasExtSizes.value) {
    await updateExtSizesAndDimsInfo()
    selectedSizeIndex.value = selectedSizeState?.value?.value ?? 0

    //Save selectedSizeState to localStorage (for extSizes with same global SKU after refresh)
    // Right now its not needed because we reset to default size after refresh.
    // if (selectedSizeState?.value) {
    //   localStorage.setItem(
    //     'selectedSizeState',
    //     `${selectedSizeState.value.SKU}-${selectedSizeState.value.skuWithSize}` ??
    //       ''
    //   )
    // }

    addToCartPayload.value = selectedSizeState?.value?.skuWithSize
      ? {
          name: pdpProductData.value?.title!,
          sku_code: selectedSizeState?.value?.skuWithSize,
          metadata: {
            size: `${selectedSizeState?.value?.label} (${selectedCountry.value})`,
            color: pdpProductData.value?.selectedColor ?? '',
            queryID: searchQueryID.value ?? undefined,
          },
        }
      : null
  }
}

// ADD TO CART
const { isLoading, error, addToCartPayload, addToCart } = useAddToCart()

const selectedSkuCode = computed(() => selectedSize.value?.sku ?? null)
const sizebayAddToCart = ref(false)

watch(
  [selectedSkuCode, selectedCountry],
  async ([selectedSkuCode, selectedCountry]) => {
    const queryParams = route.query

    const isProductFromPowerfrontChat =
      queryParams['insideproduct'] !== undefined &&
      queryParams['utm_source'] === 'insidechat'

    addToCartPayload.value = selectedSkuCode
      ? {
          name: pdpProductData.value?.title!,
          sku_code: selectedSkuCode,
          metadata: {
            size: `${selectedSize.value!.size!} (${selectedCountry})`,
            color: pdpProductData.value?.selectedColor ?? '',
            queryID: searchQueryID.value ?? undefined,
            product_from_chat: isProductFromPowerfrontChat,
          },
        }
      : null
    if (sizebayAddToCart.value) {
      await addToCart()
      sizebayAddToCart.value = false
      _dispatchAddToCartEvent()
    }
  },
  {
    immediate: true,
  }
)

// SCHEMA ORG
const isProductOutOfStock = computed(() => {
  return !!(
    skuSizes?.value?.every(item => item.avEcom === 0 && item.avRet === 0) &&
    sizes.value[selectedCountry.value]?.every(item => item.disabled)
  )
})

useSchemaOrg([
  defineProduct({
    name: title,
    brand: {
      '@type': 'Brand',
      name:
        brand.value === 'EA7'
          ? brand.value
          : startCase(brand.value.toLowerCase()),
    },
    image:
      rawImages.value?.length && rawImages.value.length > 0
        ? rawImages.value
        : undefined,
    description: productDetails.value.editoralDescription
      ? productDetails.value.editoralDescription
      : undefined,
    offers: [
      {
        price: amountFloat.value,
        priceCurrency: currencyCode.value,
        availability: !isProductOutOfStock.value
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      },
    ],
  }),
  ...schema3DModelURLs.value,
])

// WISHLIST
const { addOrRemoveItem } = useWishlist()
const updateWishlist = () => {
  const outOfStock = skuSizes.value?.every(
    item => item.avEcom === 0 && item.avRet === 0
  )
  const totalAvailableQty = skuSizes.value.reduce(
    (acc, { avEcom, avRet }) => acc + ((avEcom ?? 0) + (avRet ?? 0)),
    0
  )
  const oneSize =
    skuSizes.value.length === 1
      ? {
          size: skuSizes.value[0].label,
          sku: skuSizes.value[0].SKU,
          country: country.toUpperCase(),
        }
      : null
  const selectedSku = oneSize?.sku ?? selectedSkuCode.value

  const discountAmountAbsolute = calculateGADiscountAmount({
    newPrice: price.value,
    originalPrice: oldPrice?.value,
  })

  addOrRemoveItem({
    item: {
      productCode: productCode.value,
      title: title.value,
      path: productPathWithoutLocale.value,
      selectedColor: selectedColor.value ?? '',
      selectedSize: oneSize?.size ?? selectedSize.value?.size ?? '',
      selectedCountry: oneSize?.country ?? selectedCountry.value ?? '',
      selectedImage: rawImages.value?.[0] ?? '',
      productBrand: brand.value as Brand,
      price: price.value,
      categories: categories.value,
      isDiscounted: !!discount?.value,
      ...getOnlineAvailability(!outOfStock, isFallback?.value),
      discountAmount: discountAmountAbsolute,
      lastInStock: totalAvailableQty === 1,
      MFC: MFC.value,
      ...(selectedSku ? { productSku: selectedSku } : {}),
    },
    view: 'product_page',
    forceRefresh: false,
  })
}

// GA Events
type AlgoliaProductType = z.infer<typeof productSchema>

type ProductState = {
  algoliaProduct: AlgoliaProductType
  commercelayerProduct: ListResponse<Price>
}

const productStateForGA = useState<ProductState | null>('product-state-for-ga')
if (!isStalePath && productStateForGA.value) {
  const { algoliaProduct, commercelayerProduct } = productStateForGA.value

  try {
    // GA4 Tracking
    useGAViewItemEvent(algoliaProduct, commercelayerProduct, {
      waitForDatalayerSetup: true,
    })
    useGAEntityProductPage(algoliaProduct, commercelayerProduct)
    useParamPage('productPage', { item: algoliaProduct })
  } catch (e) {
    console.error('GA TRACKING ON PRODUCT PAGE:', e)
  }
}

const seoExtraData: PdpMeta = {
  type: 'pdp',
  product: title.value,
  collectionName: mainCategory.value?.[2]?.name ?? '',
  gender: productGender?.value ?? '',
  commercialMetaTitle: commercialMetaTitle?.value ?? undefined,
  commercialMetaDescription: commercialMetaDescription?.value ?? undefined,
}

if (Object.keys(seoExtraData).length)
  await useAmplienceSeo({ extraData: seoExtraData })

const currentAlgoliaIndex = useAppConfig().currentAlgoliaIndex

watch(mountedPdp, () => {
  if (hasExtSizes.value) {
    updateExtSizesAndDimsInfo()
    selectedSizeIndex.value = selectedSizeState?.value?.value ?? 0
    loadNewExtProd(selectedSizeState?.value?.SKU)
  }
  if (!isStalePath) {
    viewProduct(currentAlgoliaIndex, 'Product Detail', [productCode.value])
  }
})

const { isGiorgioArmaniThemeDeferred } = useTheme()

// PDP redesign (EIFE-3578) rolls out per website: brands without the Amplience
// flag keep the legacy template.
const { currentWebsite } = useWebsite()
const { isPdpRedesign } = useBrandsSettings()
const pdpTemplate = computed(() =>
  isPdpRedesign(currentWebsite.value)
    ? resolveComponent('TemplatesPdpRedesign')
    : resolveComponent('TemplatesPdp')
)

const _dispatchAddToCartEvent = () => {
  if (!addToCartPayload.value) return
  dispatchAddToCartEvent({ view: 'product_page' })
  addedToCartfromPDP(
    currentAlgoliaIndex,
    'Added to cart from PDP',
    [productCode.value],
    searchQueryID.value
  )
}
const { sizebayCountry } = useSizebayCountry()

//measmetize add to cart event listener
onMounted(() => {
  window.addEventListener('sizebay_add_to_cart', (e: any) => {
    const correctSizeIndex = sizes.value[sizebayCountry.value]?.findIndex(
      i => i.size === e.detail.size
    )
    if (correctSizeIndex > -1) {
      sizebayAddToCart.value = true
      selectedSizeIndex.value = correctSizeIndex
    }
  })
})
</script>

<template>
  <component
    :is="pdpTemplate"
    v-if="pdpProductData"
    v-model:selected-country="selectedCountry"
    v-model:selected-size-index="selectedSizeIndex"
    v-model:selected-color="selectedColor"
    :editorial-delivery-keys="editorialDeliveryKeys"
    :product-id="productCode"
    :selected-ext-sku="productCode"
    :main-category="pdpProductData?.mainCategory ?? []"
    :is-single-size-by-micro-cat="isSingleSizeByMicroCat"
    :micro-cat-kind="microCatKind"
    :algolia-color="pdpProductData?.algoliaColor"
    :title="pdpProductData?.title ?? ''"
    :price="pdpProductData?.price ?? ''"
    :price-cents="pdpProductData?.priceCents"
    :is-retail-exclusive="pdpProductData?.isRetailExclusive"
    :old-price="pdpProductData?.oldPrice"
    :discount="pdpProductData?.discount"
    :tags="pdpProductData?.tags"
    :ordered-gallery="frozenGallery as unknown as TResponsiveGallery"
    :is-fallback="isFallback"
    :sizes="sizes"
    :size-grid-code="SizeGridCode"
    :info-commerce="infoCommerce"
    :this-product-dims="thisProductDims"
    :ext-sizes="extSizesFormatted"
    :ext-colors="pdpProductData?.extColors"
    :product-route="productCode"
    :color-hex="pdpProductData?.colorHex"
    :selected-color-hex="pdpProductData?.selectedColorHex"
    :side-slide-description="
      pdpProductData?.productDetails?.fitDescription ?? ''
    "
    :color-options="pdpProductData?.altColorList ?? []"
    :product-details="pdpProductData?.productDetails"
    :product-care="pdpProductData?.productCare"
    :editorial-product-focus="editorialProductFocus"
    :product-focus="
      pdpProductData?.productFocus as unknown as PdpEcommerceBlockProductFocusSideSlideProps
    "
    :pdp-message="pdpProductData?.pdpMessage ?? undefined"
    :product-pdf="pdpProductData?.productPdf ?? ''"
    :brand="pdpProductData?.brand ?? ''"
    :product-gender="productGender"
    :sku-sizes="skuSizes"
    :product-categories-slugs="productCategoriesSlugs"
    :upcs="pdpProductData?.upcs"
    :has-virtual-mirror="pdpProductData?.hasVirtualMirror"
    :add-to-cart-props="{
      isLoading: isLoading,
      error,
    }"
    :final-sales="finalSales"
    :is-friends-family="isFriendsFamily"
    :fitting="pdpProductData?.fitting"
    :is-denim="pdpProductData?.isDenim"
    :sku-selected="selectedSkuCode ?? productCode"
    :offset-top="isGiorgioArmaniThemeDeferred"
    @update:selected-ext-sku="loadNewExtProd"
    @update-wishlist="updateWishlist"
    @add-to-cart="_dispatchAddToCartEvent"
  />
</template>
